import { useParams, Link } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCallDetail } from '../api/telemetry'
import { useMe } from '../api/auth'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { CallMetadata } from '../components/call-detail/CallMetadata'
import { CallMetrics } from '../components/call-detail/CallMetrics'
import { CostBreakdownPanel } from '../components/call-detail/CostBreakdownPanel'
import { CallChargePanel } from '../components/call-detail/CallChargePanel'
import { CallInsightsPanel } from '../components/call-detail/CallInsightsPanel'
import { PendingProposalsPanel } from '../components/call-detail/PendingProposalsPanel'
import { FullTranscript } from '../components/call-detail/FullTranscript'
import { CallEventLog } from '../components/call-detail/CallEventLog'
import { CopilotSessionView } from '../components/copilot/CopilotSessionView'

export function CallDetailPage() {
  const { t } = useTranslation('calls')
  const { callSid } = useParams()
  const { data, isLoading, isError, refetch } = useCallDetail(callSid)
  const { data: me } = useMe()
  const isSuperadmin = !!me?.is_superadmin

  if (isLoading) {
    return <SpinLoader label={t('detail.loading')} />
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Link to="/app" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-primary">
          <ArrowLeft className="h-4 w-4" />
          {t('detail.backToDashboard')}
        </Link>
        <ErrorCard
          message={t('detail.notFound')}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  const { call, events } = data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <Link
          to="/app"
          className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('detail.backToDashboard')}
        </Link>
        <h1 className="text-xl font-bold text-text-primary md:text-2xl">{t('detail.title')}</h1>
      </div>

      {/* Metadata */}
      <CallMetadata call={call} />

      {/* Server-extracted insights — interest score, outcome, reasoning. */}
      <CallInsightsPanel call={call} />

      {/* Pending rep approvals: proposed contact name update + callback. */}
      <PendingProposalsPanel call={call} />

      {/* Co-pilot calls get a full read-only reconstruction of the live
          session (transcript, sentiment timeline, coaching, insights),
          rebuilt from the persisted call_events. */}
      {call.mode === 'copilot' && (
        <CopilotSessionView callSid={call.call_sid} events={events} />
      )}

      {/* Charge / cost panel — superadmins see the full provider breakdown,
          regular org users see only the customer-facing charge. */}
      {isSuperadmin
        ? call.cost_usd != null && call.cost_usd > 0 && (
            <CostBreakdownPanel costUsd={call.cost_usd} costBreakdown={call.cost_breakdown} />
          )
        : <CallChargePanel price={call.price_usd} />}

      {/* Metrics */}
      <CallMetrics call={call} events={events} />

      {/* Transcript & Events side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FullTranscript callSid={call.call_sid} recordingUrl={call.recording_url} callSummary={call.call_summary} />
        <CallEventLog events={events} />
      </div>
    </div>
  )
}
